// SPDX-License-Identifier: FSL-1.1-Apache-2.0
/**
 * @module EventExplorerPage
 * @package @geenius/analytics/react-css
 * @description Page-level React CSS composition for filtering, inspecting, and
 * exporting analytics events.
 */

import type { AnalyticsEvent, DeviceInfo, EventType } from "@geenius/analytics";
import { buildAnalyticsSeoMeta } from "@geenius/analytics";
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  EmptyState,
} from "@geenius/ui/react-css";
import type React from "react";
import { useMemo, useState } from "react";
import { EventTable } from "../components/EventTable";
import { AnalyticsPageErrorBoundary } from "../components/AnalyticsPageErrorBoundary";
import {
  type AnalyticsErrorStateValue,
  ErrorState,
} from "../components/ErrorState";
import { useAnalyticsCopy } from "../lib/analyticsCopy";
import { cx } from "../lib/cx";
import { FadeIn } from "../lib/FadeIn";
import type { AnalyticsSeoMeta } from "./AnalyticsDashboardPage";

export interface EventExplorerFilters {
  types?: EventType[];
  startTime?: number;
  endTime?: number;
  url?: string;
  userId?: string;
  sessionId?: string;
  device?: DeviceInfo["device"];
  search?: string;
}

/**
 * Props for rendering the React CSS event explorer page.
 *
 * @property events Event rows available to browse and export.
 * @property initialFilters Optional initial filters for the local controls.
 * @property onFiltersChange Called whenever local filters change.
 * @property onExport Called with the current filtered event rows.
 * @property isLoading Whether the table should render loading placeholders.
 * @property error Optional page-level error surfaced before event data sections.
 * @property onRetry Optional recovery callback shown in the page-level error state.
 * @property maxRows Maximum number of table rows to render.
 * @property renderSeoMeta Optional renderer for host apps that mount SEO metadata.
 * @property className Optional class name merged into the page wrapper.
 */
export interface EventExplorerPageProps {
  events?: AnalyticsEvent[];
  initialFilters?: EventExplorerFilters;
  onFiltersChange?: (filters: EventExplorerFilters) => void;
  onExport?: (events: AnalyticsEvent[]) => void;
  isLoading?: boolean;
  error?: AnalyticsErrorStateValue;
  onRetry?: () => void;
  maxRows?: number;
  renderSeoMeta?: (meta: AnalyticsSeoMeta) => React.ReactNode;
  className?: string;
}

function toDateInputValue(timestamp?: number): string {
  if (!timestamp) return "";
  return new Date(timestamp).toISOString().slice(0, 10);
}

function fromDateInputValue(
  value: string,
  boundary: "start" | "end",
): number | undefined {
  if (!value) return undefined;
  const date = new Date(
    `${value}T${boundary === "start" ? "00:00:00" : "23:59:59"}`,
  );
  return Number.isNaN(date.getTime()) ? undefined : date.getTime();
}

function includesText(value: string | undefined, query: string): boolean {
  return value?.toLowerCase().includes(query) ?? false;
}

function eventMatchesFilters(
  event: AnalyticsEvent,
  filters: EventExplorerFilters,
): boolean {
  if (filters.types?.length && !filters.types.includes(event.type))
    return false;
  if (filters.startTime && event.timestamp < filters.startTime) return false;
  if (filters.endTime && event.timestamp > filters.endTime) return false;
  if (filters.device && event.device.device !== filters.device) return false;

  const urlQuery = filters.url?.trim().toLowerCase();
  if (urlQuery && !includesText(event.url, urlQuery)) return false;

  const userQuery = filters.userId?.trim().toLowerCase();
  if (userQuery && !includesText(event.userId, userQuery)) return false;

  const sessionQuery = filters.sessionId?.trim().toLowerCase();
  if (sessionQuery && !includesText(event.sessionId, sessionQuery))
    return false;

  const searchQuery = filters.search?.trim().toLowerCase();
  if (searchQuery) {
    const searchable = [
      event.name,
      event.type,
      event.url,
      event.userId,
      event.sessionId,
      event.device.browser,
      event.device.os,
      JSON.stringify(event.properties ?? {}),
    ].join(" ");

    if (!searchable.toLowerCase().includes(searchQuery)) return false;
  }

  return true;
}

/**
 * Renders an event explorer with filters, property inspection, and export.
 *
 * @param props Event rows, initial filters, and export callback.
 * @returns A React CSS page composition for event exploration workflows.
 */
export function EventExplorerPage({
  events = [],
  initialFilters,
  onFiltersChange,
  onExport,
  isLoading,
  error,
  onRetry,
  maxRows = 25,
  renderSeoMeta,
  className,
}: EventExplorerPageProps): React.ReactElement {
  const copy = useAnalyticsCopy();
  const [filters, setFilters] = useState<EventExplorerFilters>(
    initialFilters ?? {},
  );
  const [selectedEventId, setSelectedEventId] = useState<string>("");
  const seoMeta = buildAnalyticsSeoMeta({
    title: copy.fallback("analytics.seo.eventExplorerTitle", "Event Explorer"),
    description: copy.fallback(
      "analytics.seo.eventExplorerDescription",
      "Browse, filter, inspect, and export analytics events for product analysis.",
    ),
    path: "/analytics/events",
    keywords: ["analytics", "events", "filters", "export"],
  });
  const filteredEvents = useMemo(
    () => events.filter((event) => eventMatchesFilters(event, filters)),
    [events, filters],
  );
  const inspectedEvent =
    filteredEvents.find((event) => event.id === selectedEventId) ??
    filteredEvents[0] ??
    null;

  const updateFilters = (patch: Partial<EventExplorerFilters>): void => {
    const next = { ...filters, ...patch };
    setFilters(next);
    onFiltersChange?.(next);
  };

  return (
    <>
      {renderSeoMeta ? renderSeoMeta(seoMeta) : null}
      <AnalyticsPageErrorBoundary onRetry={onRetry}>
        <div
          className={cx(
            "gn-analytics-page",
            "gn-analytics-event-explorer",
            className,
          )}
          data-analytics-page="event-explorer"
        >
          <FadeIn>
            <header
              className="gn-analytics-page__header"
              data-analytics-page-header
            >
              <h1 className="gn-analytics-page__title">
                {copy.fallback(
                  "analytics.events.explorerTitle",
                  "Event Explorer",
                )}
              </h1>
              <p className="gn-analytics-page__subtitle">
                {copy.fallback(
                  "analytics.events.explorerSubtitle",
                  "Filter tracked events, inspect properties, and export the current result set.",
                )}
              </p>
            </header>
          </FadeIn>

          {error ? (
            <FadeIn delay={60}>
              <Card data-analytics-page-error>
                <CardContent>
                  <ErrorState error={error} onRetry={onRetry} />
                </CardContent>
              </Card>
            </FadeIn>
          ) : (
            <>
              <FadeIn delay={60}>
                <Card data-analytics-event-filters>
                  <CardHeader>
                    <CardTitle data-analytics-section-title>
                      {copy.fallback(
                        "analytics.events.filters.title",
                        "Filters",
                      )}
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="gn-analytics-event-explorer__filters">
                    <label className="gn-analytics-event-explorer__filter">
                      <span>{copy.t("analytics.events.filters.search")}</span>
                      <input
                        className="gn-analytics-event-explorer__input"
                        value={filters.search ?? ""}
                        onChange={(event) =>
                          updateFilters({ search: event.currentTarget.value })
                        }
                        placeholder={copy.fallback(
                          "analytics.events.filters.explorerSearchPlaceholder",
                          "event, user, URL, property",
                        )}
                      />
                    </label>
                    <label className="gn-analytics-event-explorer__filter">
                      <span>{copy.t("analytics.events.filters.type")}</span>
                      <select
                        className="gn-analytics-event-explorer__select"
                        value={filters.types?.[0] ?? ""}
                        onChange={(event) =>
                          updateFilters({
                            types: event.currentTarget.value
                              ? [event.currentTarget.value as EventType]
                              : undefined,
                          })
                        }
                      >
                        <option value="">
                          {copy.t("analytics.events.filters.allTypes")}
                        </option>
                        <option value="page_view">
                          {copy.t("analytics.events.types.pageView")}
                        </option>
                        <option value="click">
                          {copy.t("analytics.events.types.click")}
                        </option>
                        <option value="form_submit">
                          {copy.t("analytics.events.types.formSubmit")}
                        </option>
                        <option value="custom">
                          {copy.t("analytics.events.types.custom")}
                        </option>
                      </select>
                    </label>
                    <label className="gn-analytics-event-explorer__filter">
                      <span>{copy.t("analytics.events.filters.url")}</span>
                      <input
                        className="gn-analytics-event-explorer__input"
                        value={filters.url ?? ""}
                        onChange={(event) =>
                          updateFilters({ url: event.currentTarget.value })
                        }
                        placeholder={copy.fallback(
                          "analytics.events.filters.urlPlaceholder",
                          "/pricing",
                        )}
                      />
                    </label>
                    <label className="gn-analytics-event-explorer__filter">
                      <span>{copy.t("analytics.events.filters.user")}</span>
                      <input
                        className="gn-analytics-event-explorer__input"
                        value={filters.userId ?? ""}
                        onChange={(event) =>
                          updateFilters({ userId: event.currentTarget.value })
                        }
                        placeholder={copy.fallback(
                          "analytics.events.filters.userPlaceholder",
                          "user_",
                        )}
                      />
                    </label>
                    <label className="gn-analytics-event-explorer__filter">
                      <span>{copy.t("analytics.events.filters.session")}</span>
                      <input
                        className="gn-analytics-event-explorer__input"
                        value={filters.sessionId ?? ""}
                        onChange={(event) =>
                          updateFilters({
                            sessionId: event.currentTarget.value,
                          })
                        }
                        placeholder={copy.fallback(
                          "analytics.events.filters.sessionPlaceholder",
                          "ses_",
                        )}
                      />
                    </label>
                    <label className="gn-analytics-event-explorer__filter">
                      <span>{copy.t("analytics.events.filters.device")}</span>
                      <select
                        className="gn-analytics-event-explorer__select"
                        value={filters.device ?? ""}
                        onChange={(event) =>
                          updateFilters({
                            device: event.currentTarget.value
                              ? (event.currentTarget
                                  .value as DeviceInfo["device"])
                              : undefined,
                          })
                        }
                      >
                        <option value="">
                          {copy.t("analytics.events.filters.allDevices")}
                        </option>
                        <option value="desktop">
                          {copy.t("analytics.devices.desktop")}
                        </option>
                        <option value="tablet">
                          {copy.t("analytics.devices.tablet")}
                        </option>
                        <option value="mobile">
                          {copy.t("analytics.devices.mobile")}
                        </option>
                      </select>
                    </label>
                    <label className="gn-analytics-event-explorer__filter">
                      <span>{copy.t("analytics.events.filters.from")}</span>
                      <input
                        className="gn-analytics-event-explorer__input"
                        type="date"
                        value={toDateInputValue(filters.startTime)}
                        onChange={(event) =>
                          updateFilters({
                            startTime: fromDateInputValue(
                              event.currentTarget.value,
                              "start",
                            ),
                          })
                        }
                      />
                    </label>
                    <label className="gn-analytics-event-explorer__filter">
                      <span>{copy.t("analytics.events.filters.to")}</span>
                      <input
                        className="gn-analytics-event-explorer__input"
                        type="date"
                        value={toDateInputValue(filters.endTime)}
                        onChange={(event) =>
                          updateFilters({
                            endTime: fromDateInputValue(
                              event.currentTarget.value,
                              "end",
                            ),
                          })
                        }
                      />
                    </label>
                  </CardContent>
                </Card>
              </FadeIn>

              <div className="gn-analytics-event-explorer__workspace">
                <FadeIn delay={120}>
                  <div className="gn-analytics-event-explorer__toolbar">
                    <span className="gn-analytics-event-explorer__summary">
                      {filteredEvents.length.toLocaleString()}{" "}
                      {copy.fallback("analytics.events.summaryLabel", "events")}
                    </span>
                    <Button
                      type="button"
                      variant="secondary"
                      disabled={filteredEvents.length === 0}
                      onClick={() => onExport?.(filteredEvents)}
                    >
                      {copy.fallback("analytics.actions.export", "Export")}
                    </Button>
                  </div>
                  <EventTable
                    events={filteredEvents}
                    isLoading={isLoading}
                    maxRows={maxRows}
                    showControls={false}
                  />
                </FadeIn>

                <FadeIn delay={180}>
                  <Card data-analytics-event-inspector>
                    <CardHeader>
                      <CardTitle data-analytics-section-title>
                        {copy.fallback(
                          "analytics.events.inspector.title",
                          "Property inspection",
                        )}
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="gn-analytics-event-explorer__inspector">
                      {filteredEvents.length === 0 ? (
                        <EmptyState
                          title={copy.fallback(
                            "analytics.events.inspector.empty",
                            "No event selected.",
                          )}
                        />
                      ) : (
                        <>
                          <label className="gn-analytics-event-explorer__filter">
                            <span>
                              {copy.fallback(
                                "analytics.events.inspector.selectedEvent",
                                "Selected event",
                              )}
                            </span>
                            <select
                              className="gn-analytics-event-explorer__select"
                              value={inspectedEvent?.id ?? ""}
                              onChange={(event) =>
                                setSelectedEventId(event.currentTarget.value)
                              }
                            >
                              {filteredEvents.map((event) => (
                                <option key={event.id} value={event.id}>
                                  {event.name}
                                </option>
                              ))}
                            </select>
                          </label>
                          <dl className="gn-analytics-event-explorer__details">
                            <div>
                              <dt>
                                {copy.t("analytics.events.filters.session")}
                              </dt>
                              <dd>{inspectedEvent?.sessionId}</dd>
                            </div>
                            <div>
                              <dt>{copy.t("analytics.events.filters.user")}</dt>
                              <dd>
                                {inspectedEvent?.userId ??
                                  copy.fallback(
                                    "analytics.events.inspector.anonymous",
                                    "anonymous",
                                  )}
                              </dd>
                            </div>
                            <div>
                              <dt>
                                {copy.t("analytics.events.filters.device")}
                              </dt>
                              <dd>
                                {inspectedEvent
                                  ? copy.deviceLabel(
                                      inspectedEvent.device.device,
                                    )
                                  : null}
                              </dd>
                            </div>
                          </dl>
                          <pre className="gn-analytics-event-explorer__properties">
                            {JSON.stringify(
                              inspectedEvent?.properties ?? {},
                              null,
                              2,
                            )}
                          </pre>
                        </>
                      )}
                    </CardContent>
                  </Card>
                </FadeIn>
              </div>
            </>
          )}
        </div>
      </AnalyticsPageErrorBoundary>
    </>
  );
}
